/**
 * The company Member field's arithmetic (CompanyMemberPickerField.vue), kept out
 * of the .vue so it can be tested.
 */

/** As many addresses as the Teams listing takes at once in `member_email`. */
export const MEMBER_EMAIL_FILTER_MAX_VALUES = 100;

// Something, an @, and a domain with a dot: enough to tell an address from a
// half-typed search, without refusing any address a company member could have.
const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

/**
 * The field's list: the picked addresses first, in the order they were picked,
 * so they stay on screen whatever the search lists, then the matches. Each
 * address appears once, whatever its case, since the server matches addresses
 * that way.
 */
export function memberEmailPickerItems(picked: string[] | null | undefined, found: string[]): string[] {
	const seen: Record<string, true> = {};
	const out: string[] = [];
	[...(picked || []), ...found].forEach((email: string | null | undefined) => {
		const trimmed: string = (email || '').trim();
		if (!trimmed) return;
		const key: string = trimmed.toLowerCase();
		if (seen[key]) return;
		seen[key] = true;
		out.push(trimmed);
	});
	return out;
}

export interface EmailFieldEntries {
	// What the field holds: each address once, whatever its case, in the order entered
	addresses: string[];
	// What could not be: text that is not an address, or addresses past the cap
	rejected: string[];
}

/**
 * Sort what the field was given into the addresses it keeps and the text it
 * gives back. An entry may be a picked address or typed text, and typed text
 * may hold several addresses (pasted, separated by commas, semicolons or
 * spaces), so each is split before it is checked.
 */
export function emailFieldEntries(entries: string[], max: number = MEMBER_EMAIL_FILTER_MAX_VALUES): EmailFieldEntries {
	const seen: Record<string, true> = {};
	const addresses: string[] = [];
	const rejected: string[] = [];
	entries.forEach((entry: string) => {
		(entry || '').split(/[\s,;]+/).forEach((part: string) => {
			const candidate: string = part.trim();
			if (!candidate) return;
			if (!EMAIL_PATTERN.test(candidate)) {
				rejected.push(candidate);
				return;
			}
			const key: string = candidate.toLowerCase();
			if (seen[key]) return;
			if (addresses.length >= max) {
				rejected.push(candidate);
				return;
			}
			seen[key] = true;
			addresses.push(candidate);
		});
	});
	return { addresses, rejected };
}
